import React from 'react';
import { Link } from 'react-router-dom';
import { Image, Palette, PartyPopper } from 'lucide-react';
import { PageLayout } from './layouts/PageLayout';
import { Navigation } from './Navigation';
import { Footer } from './Footer';

const GalleryLanding = () => {
  const galleries = [
    {
      id: 'main',
      title: 'Main Gallery',
      description: 'Explore our visual journey and moments',
      icon: Image,
      color: 'orange'
    },
    {
      id: 'art',
      title: 'Art Gallery',
      description: 'Discover psychedelic artwork and visuals',
      icon: Palette,
      color: 'green'
    },
    {
      id: 'party',
      title: 'Party Gallery',
      description: 'Experience our events and party vibes',
      icon: PartyPopper,
      color: 'purple'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      orange: {
        border: 'border-orange-500/30',
        hover: 'group-hover:border-orange-500',
        text: 'text-orange-500',
        bg: 'bg-orange-500/10'
      },
      green: {
        border: 'border-green-500/30',
        hover: 'group-hover:border-green-500',
        text: 'text-green-500',
        bg: 'bg-green-500/10'
      },
      purple: {
        border: 'border-purple-500/30',
        hover: 'group-hover:border-purple-500',
        text: 'text-purple-500',
        bg: 'bg-purple-500/10'
      }
    };
    return colors[color];
  };

  return (
    <>
      <Navigation />
      <PageLayout>
        <div className="min-h-screen pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 group cursor-default">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 via-green-500 to-purple-500 group-hover:from-orange-400 group-hover:via-green-400 group-hover:to-purple-400 transition-all duration-500">
                Our Galleries
              </span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto hover:text-gray-300 transition-colors duration-300">
              Immerse yourself in the visual world of DharmaR - from psychedelic art to unforgettable party moments
            </p>
          </div>

          {/* Gallery Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {galleries.map((gallery) => {
              const Icon = gallery.icon;
              const colors = getColorClasses(gallery.color);
              
              return (
                <Link
                  key={gallery.id}
                  to={`/gallery/${gallery.id}`}
                  className="group"
                >
                  <div
                    className={`relative bg-black/40 backdrop-blur-sm rounded-xl border-2 ${colors.border} ${colors.hover} transition-all duration-300 overflow-hidden h-full hover:scale-105 hover:shadow-2xl`}
                  >
                    {/* Icon Background */}
                    <div className={`absolute top-0 right-0 w-32 h-32 ${colors.bg} rounded-bl-full opacity-50 group-hover:opacity-70 transition-opacity`} />
                    
                    {/* Content */}
                    <div className="relative p-8 space-y-6">
                      {/* Icon */}
                      <div className={`w-20 h-20 ${colors.bg} rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform`}>
                        <Icon size={40} className={colors.text} />
                      </div>

                      {/* Title */}
                      <h3 className={`text-2xl font-bold ${colors.text} group-hover:scale-105 transform transition-all duration-300 group-hover:brightness-125`}>
                        {gallery.title}
                      </h3>

                      {/* Description */}
                      <p className="text-gray-400 text-base group-hover:text-gray-200 transition-colors duration-300">
                        {gallery.description}
                      </p>

                      {/* Arrow */}
                      <div className="flex items-center gap-2 text-gray-400 group-hover:text-white transition-all duration-300">
                        <span className="text-sm font-semibold group-hover:tracking-wider transition-all duration-300">Explore</span>
                        <svg
                          className="w-4 h-4 group-hover:translate-x-2 transition-transform duration-300"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
      </PageLayout>
      <Footer />
    </>
  );
};

export default GalleryLanding;
